import { Center, For, Grid, GridItem, Text } from '@chakra-ui/react';
import Player from '@components/common/video/Player';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectOngoingNotificationsBySegmentName } from '@redux/data/data.selectors';
import { selectSelectedDoubtCheckSegmentName } from '@redux/global/global.selector';
import {
  selectPerimeterCamerasBySegmentName,
  selectSelectedDoubtCheckPerimeterCamera,
} from '@redux/sensors/sensors.selectors';
import { useUpdatePerimeterCameraConfigurationMutation } from '@services/c2/c2.api';
import { formatDate } from '@utils/date.utils';
import { addDateToSourceReplayUrl } from '@utils/replay/replay.utils';
import { getNimbleStreamFromUrl } from '@utils/sensors/camera/camera.utils';
import { subSeconds } from 'date-fns';
import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { SegmentNotification } from '@/types/data/data.types';
import { DateTimeEnum } from '@/types/dateTime.types';
import { PerimeterCamera } from '@/types/sensor/sensors/perimeterCamera.types';

import PerimeterCameraWrapper from './PerimeterCameraWrapper';

interface OwnProps {
  uniqueKey: string;
}

function PerimeterDoubtCheckGrid({ uniqueKey }: Readonly<OwnProps>) {
  const [urls, setUrls] = useState(new Map<string, string>());
  const { formatMessage } = useIntl();

  const singlePerimeterCamera = useAppSelector(selectSelectedDoubtCheckPerimeterCamera, isEqual);

  useEffect(() => {
    if (singlePerimeterCamera && !singlePerimeterCamera.url) {
      createToast(
        formatMessage({ id: 'automatonSection.doubtCheck.noUrl' }, { cam: singlePerimeterCamera.uniqueKey }),
        ToastStatusEnum.ERROR,
      );
    }
  }, [formatMessage, singlePerimeterCamera]);

  const segmentName: string | null = useAppSelector(selectSelectedDoubtCheckSegmentName);

  const effractionNotifications: SegmentNotification[] = useAppSelector(
    (state) => selectOngoingNotificationsBySegmentName(state, segmentName ?? ''),
    isEqual,
  );
  const segmentPerimeterCameras: PerimeterCamera[] = useAppSelector(
    (state) => selectPerimeterCamerasBySegmentName(state, segmentName),
    isEqual,
  );
  const segmentPerimeterCamerasChunk: PerimeterCamera[] = useMemo(
    () => segmentPerimeterCameras.slice(0, 4),
    [segmentPerimeterCameras],
  );

  const [updatePericam] = useUpdatePerimeterCameraConfigurationMutation();

  useEffect(() => {
    const tmpUrls = new Map<string, string>();
    segmentPerimeterCamerasChunk.forEach((pericam) => {
      let url = pericam.url ? getNimbleStreamFromUrl(pericam.url) : null;
      if (effractionNotifications.length > 0) {
        const notification = effractionNotifications[0];
        if (pericam.sourceReplayUrl) {
          const newReplayUrl = addDateToSourceReplayUrl(
            pericam.sourceReplayUrl,
            formatDate(subSeconds(new Date(notification.startTime), 5), DateTimeEnum.DATE_TIME_SECONDS_PERIM_FORMAT),
          );
          if (pericam.sourceReplayUrl != newReplayUrl) {
            updatePericam({
              id: pericam.id,
              site: pericam.site,
              pericam: {
                ...pericam,
                sourceReplayUrl: newReplayUrl,
              },
            });
            url = null;
          } else if (pericam.replayUrl) {
            url = pericam.replayUrl;
          } else {
            createToast(
              formatMessage({ id: 'automatonSection.doubtCheck.noReplayUrl' }, { cam: pericam.uniqueKey }),
              ToastStatusEnum.ERROR,
            );
          }
        }
      }
      if (!url) {
        createToast(
          formatMessage({ id: 'automatonSection.doubtCheck.noUrl' }, { cam: pericam.uniqueKey }),
          ToastStatusEnum.ERROR,
        );
      } else {
        tmpUrls.set(pericam.uniqueKey, url);
      }
      setUrls(tmpUrls);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segmentName, effractionNotifications, segmentPerimeterCamerasChunk, formatMessage]);

  if (singlePerimeterCamera?.url) {
    return (
      <PerimeterCameraWrapper
        name={singlePerimeterCamera.name}
        position={singlePerimeterCamera.coordinates}
        showToolBar
      >
        <Player src={getNimbleStreamFromUrl(singlePerimeterCamera.url)} id={`player-${uniqueKey}-pericam`} />
      </PerimeterCameraWrapper>
    );
  }

  if (!segmentName) {
    return (
      <Center width="100%" height="100%">
        <Text>
          <FormattedMessage id="automatonSection.doubtCheck.noFenceSelected" />
        </Text>
      </Center>
    );
  }

  if (segmentPerimeterCamerasChunk.length === 0) {
    return (
      <Center width="100%" height="100%">
        <Text>
          <FormattedMessage id="automatonSection.doubtCheck.noCameraLinkedToFence" />
        </Text>
      </Center>
    );
  }

  return (
    <Grid
      templateAreas={`"stream-2 stream-0""stream-3 stream-1"`}
      templateColumns="1fr 1fr"
      templateRows="1fr 1fr"
      minHeight={0}
      minWidth={0}
      width="100%"
      height="100%"
    >
      <For each={segmentPerimeterCamerasChunk}>
        {(cam, index) => (
          <GridItem key={`${uniqueKey}-${cam.uniqueKey}`} minHeight={0} minWidth={0} area={`stream-${index}`}>
            <PerimeterCameraWrapper name={cam.name} position={cam.coordinates} showToolBar>
              <Player src={urls.get(cam.uniqueKey) ?? ''} id={`player-${uniqueKey}-${cam.uniqueKey}`} />
            </PerimeterCameraWrapper>
          </GridItem>
        )}
      </For>
    </Grid>
  );
}

export default PerimeterDoubtCheckGrid;
